/* Source-bound Layout C badges. Only known Green Stitches photos and observed photo surfaces. */
(() => {
  'use strict';
  if (window.gsnPhotoCreditLoaded) return;
  window.gsnPhotoCreditLoaded = true;
  const selector = [
    '.gsn-card__photo > img', '.gsn-view__main > img', '.gsn-thumbs button > img',
    '.cart-item__image', '.predictive-search__image', 'lb-upsell-flat-card.lb-product-card img',
    '.gsn-home-hero .gs-sw-hero__media img', '.gsn-home-statement .gs-sw-statement__media img',
    '.gsn-home-index .gs-sw-index__figure img', '.gsn-home-editorial .gs-sw-editorial__lead img',
    '.gsn-home-editorial .gs-sw-editorial__side img', '.gsn-home-ort .gs-sw-ort__media img',
    '.product__media img', '.product-media-modal__content img'
  ].join(',');
  const records = new Map();
  let queued = false;
  function classify(source) {
    let name;
    try { name = decodeURIComponent(new URL(source, location.href).pathname.split('/').pop()).toLowerCase(); }
    catch (_) { return null; }
    name = name.replace(/_\d*x\d*(?:_crop_[a-z]+)?(?=\.(?:png|jpe?g|webp)$)/, '');
    if (/^gsn-[a-z0-9-]+-ki\.(?:png|jpe?g|webp)$/.test(name) ||
        /^gsn-basic-t-(?:black|khaki)-originalfreistellung-v1\.(?:png|jpe?g|webp)$/.test(name)) return 'ai';
    if (/^gsn-[a-z0-9-]+-(?:original-(?:front|back|open)|detail-\d+)\.(?:png|jpe?g|webp)$/.test(name) ||
        name === 'gsn-ortsanker-poster.png') return 'human';
    return null;
  }
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  }
  const resizer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
  function remove(img, record) {
    record.badge.remove();
    if (resizer) resizer.unobserve(img);
    records.delete(img);
  }
  function objectOffset(value, space) {
    if (value === 'left' || value === 'top') return 0;
    if (value === 'right' || value === 'bottom') return space;
    if (value === 'center') return space / 2;
    if (/^[+-]?[\d.]+%$/.test(value)) return space * parseFloat(value) / 100;
    if (/^[+-]?[\d.]+(?:px)?$/.test(value)) return parseFloat(value);
    const calc = value.match(/^calc\(\s*([+-]?[\d.]+)%\s*([+-])\s*([\d.]+)px\s*\)$/);
    if (calc) return space * parseFloat(calc[1]) / 100 + (calc[2] === '-' ? -1 : 1) * parseFloat(calc[3]);
    return space / 2;
  }
  function photoRect(img, rect) {
    const style = getComputedStyle(img);
    if (style.objectFit !== 'contain' && style.objectFit !== 'scale-down') return rect;
    let scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
    if (style.objectFit === 'scale-down') scale = Math.min(1, scale);
    const width = img.naturalWidth * scale;
    const height = img.naturalHeight * scale;
    const position = (style.objectPosition || '50% 50%').match(/calc\([^)]*\)|\S+/g) || ['50%', '50%'];
    let x = position[0];
    let y = position[1] || '50%';
    if (position.length === 1 && (x === 'top' || x === 'bottom')) { y = x; x = '50%'; }
    if ((x === 'top' || x === 'bottom') && (y === 'left' || y === 'right' || y === 'center')) [x, y] = [y, x];
    let offsetX = objectOffset(x, rect.width - width);
    let offsetY = objectOffset(y, rect.height - height);
    if (position.length === 4 && /^(left|right)$/.test(position[0]) && /^(top|bottom)$/.test(position[2])) {
      offsetX = (position[0] === 'right' ? rect.width - width : 0) + (position[0] === 'right' ? -1 : 1) * objectOffset(position[1], rect.width - width);
      offsetY = (position[2] === 'bottom' ? rect.height - height : 0) + (position[2] === 'bottom' ? -1 : 1) * objectOffset(position[3], rect.height - height);
    }
    const left = rect.left + offsetX;
    const top = rect.top + offsetY;
    const clippedLeft = Math.max(rect.left, left);
    const clippedTop = Math.max(rect.top, top);
    const right = Math.min(rect.right, left + width);
    const bottom = Math.min(rect.bottom, top + height);
    return { left: clippedLeft, top: clippedTop, right, bottom, width: Math.max(0, right - clippedLeft), height: Math.max(0, bottom - clippedTop) };
  }
  function place(img, record) {
    const rect = img.getBoundingClientRect();
    const owner = record.owner;
    const box = owner.getBoundingClientRect();
    if (!img.complete || !img.naturalWidth || !rect.width || !rect.height) { record.badge.hidden = true; return; }
    const photo = photoRect(img, rect);
    const style = getComputedStyle(owner);
    const clips = /(auto|scroll|hidden|clip)/.test(style.overflowX + style.overflowY);
    const right = clips ? Math.min(photo.right, box.right - owner.clientLeft) : photo.right;
    const bottom = clips ? Math.min(photo.bottom, box.bottom - owner.clientTop) : photo.bottom;
    const left = clips ? Math.max(photo.left, box.left + owner.clientLeft) : photo.left;
    const top = clips ? Math.max(photo.top, box.top + owner.clientTop) : photo.top;
    const visibleWidth = Math.max(0, right - left);
    const visibleHeight = Math.max(0, bottom - top);
    if (!visibleWidth || !visibleHeight) { record.badge.hidden = true; return; }
    record.badge.hidden = false;
    const inset = Math.min(12, Math.max(3, visibleWidth * .02));
    const ratio = (record.kind === 'ai' ? 313 : 260) / 555;
    const width = Math.max(0, Math.min(110, visibleWidth * .20, visibleWidth - inset * 2, (visibleHeight - inset * 2) / ratio));
    if (!width) { record.badge.hidden = true; return; }
    const height = width * ratio;
    Object.assign(record.badge.style, {
      width: width + 'px',
      left: (right - box.left - owner.clientLeft + owner.scrollLeft - inset - width) + 'px',
      top: (bottom - box.top - owner.clientTop + owner.scrollTop - inset - height) + 'px'
    });
    record.badge.dataset.compact = String(width < 94);
  }
  function update() {
    queued = false;
    records.forEach((record, img) => {
      if (!img.isConnected || !img.matches(selector)) remove(img, record);
    });
    document.querySelectorAll(selector).forEach(img => {
      const kind = classify(img.currentSrc || img.getAttribute('src') || '');
      let record = records.get(img);
      if (!kind) { if (record) remove(img, record); return; }
      const owner = img.parentElement.tagName === 'PICTURE' ? img.parentElement.parentElement : img.parentElement;
      if (!owner) return;
      if (record && (record.kind !== kind || record.owner !== owner)) { remove(img, record); record = null; }
      if (!record) {
        const template = document.getElementById('GSNPhotoCredit-' + kind);
        if (!template) return;
        const badge = template.content.firstElementChild.cloneNode(true);
        if (getComputedStyle(owner).position === 'static') owner.classList.add('gsn-photo-credit-owner');
        owner.appendChild(badge);
        record = { badge, kind, owner };
        records.set(img, record);
        if (resizer) resizer.observe(img);
      }
      place(img, record);
    });
  }
  new MutationObserver(schedule).observe(document.documentElement, {
    subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset']
  });
  document.addEventListener('load', schedule, true);
  document.addEventListener('scroll', schedule, { capture: true, passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  document.addEventListener('shopify:section:load', schedule);
  schedule();
})();
